import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AuthContext } from '../App';
import { 
  ArrowLeft, Plane, Award, BookOpen, Mic, Users, Shield,
  Volume2, MessageSquare, ChevronRight, Lock, CheckCircle2,
  Star, Zap, Target, Crown, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVEL_COLORS = {
  'A1-A2': { bg: 'from-green-400 to-emerald-500', border: 'border-green-400', text: 'text-green-600' },
  'B1-B2': { bg: 'from-blue-400 to-indigo-500', border: 'border-blue-400', text: 'text-blue-600' },
  'C1-C2': { bg: 'from-purple-400 to-pink-500', border: 'border-purple-400', text: 'text-purple-600' }
};

const MODULE_ICONS = {
  'aviation_vocabulary': '✈️',
  'icao_alphabet_basic': '🔤',
  'icao_alphabet_intermediate': '🔤',
  'greetings_intro': '👋',
  'cabin_objects': '💺',
  'passenger_dialogues_basic': '💬',
  'safety_commands_basic': '🛡️',
  'interview_basic': '🎤',
  'aircraft_structure': '🛩️',
  'boarding_interaction': '🚪',
  'inflight_service': '🍽️',
  'complaint_handling': '😤',
  'safety_emergency': '🚨',
  'pa_announcements': '📢',
  'interview_intermediate': '🎯',
  'professional_communication': '💼',
  'complex_scenarios': '🎭',
  'leadership_safety': '👨‍✈️',
  'cultural_sensitivity': '🌍',
  'medical_advanced': '🏥',
  'assessment_day': '🏆',
  'interview_advanced': '✨'
};

const CabinCrewModules = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState({});
  const [progress, setProgress] = useState({});
  const [selectedLevel, setSelectedLevel] = useState('A1-A2');

  // Demo kullanıcıları bu sayfaya erişemez
  useEffect(() => {
    if (user?.is_demo) {
      toast.error('Bu özellik tam sürümde mevcuttur');
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user && !user.is_demo) {
      loadModules();
    }
  }, [user]);

  const loadModules = async () => {
    setLoading(true);
    try {
      const [modulesRes, progressRes] = await Promise.all([
        axios.get(`${API}/cabin-crew/modules`, { withCredentials: true }),
        axios.get(`${API}/cabin-crew/progress`, { withCredentials: true })
      ]);
      setModules(modulesRes.data.modules);
      setProgress(progressRes.data);
    } catch (error) {
      console.error('Error loading modules:', error);
      toast.error('Modüller yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const startLesson = (level, moduleId) => {
    navigate(`/cabin-crew/comprehensive/${level}/${moduleId}`);
  };

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50">
          <div className="text-center">
            <div className="w-20 h-20 border-8 border-sky-200 border-t-sky-600 rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-xl font-bold text-gray-700">Yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  const currentLevelData = modules[selectedLevel];
  const levelColors = LEVEL_COLORS[selectedLevel];
  const levelProgress = progress.level_progress?.[selectedLevel] || { completed: 0, total: 0, percentage: 0 };

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <Button 
              onClick={() => navigate('/dashboard')} 
              variant="ghost" 
              className="text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Ana Sayfa
            </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-sky-100 rounded-full">
              <Zap className="w-5 h-5 text-sky-500" />
              <span className="font-bold text-sky-700">{progress.total_miles || 0} Mil</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-sky-500 to-indigo-600 rounded-full shadow-lg mb-4">
            <Plane className="w-8 h-8 text-white" />
            <span className="text-xl font-black text-white">Kabin Memurluğu Eğitimi</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black">
            <span className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Havacılık İngilizcesi & Mülakat Hazırlığı
            </span>
          </h1>
          <p className="text-gray-600 mt-2">ICAO Alfabe, Diyaloglar, Anonslar ve Mülakat Simülasyonu</p>
        </div>

        {/* Level Tabs */}
        <div className="flex justify-center gap-3 mb-8">
          {Object.keys(LEVEL_COLORS).map((level) => {
            const colors = LEVEL_COLORS[level];
            const lProgress = progress.level_progress?.[level] || { percentage: 0 };
            return (
              <button
                key={level}
                onClick={() => setSelectedLevel(level)}
                className={`relative px-6 py-4 rounded-2xl font-bold transition-all ${
                  selectedLevel === level
                    ? `bg-gradient-to-r ${colors.bg} text-white shadow-xl scale-105`
                    : 'bg-white text-gray-600 hover:bg-gray-100 border-2 border-gray-200'
                }`}
              >
                <span className="text-lg">{level}</span>
                {lProgress.percentage > 0 && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {Math.round(lProgress.percentage)}%
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Level Info */}
        {currentLevelData && (
          <Card className={`mb-8 border-4 ${levelColors.border}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-black text-gray-800">{currentLevelData.level_name}</h2>
                  <p className="text-gray-600">{currentLevelData.level_description}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">İlerleme</p>
                  <p className={`text-3xl font-black ${levelColors.text}`}>
                    {levelProgress.completed}/{levelProgress.total}
                  </p>
                </div>
              </div>
              <Progress value={levelProgress.percentage} className="h-3 mt-4" />
            </CardContent>
          </Card>
        )}

        {/* Modules Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {currentLevelData?.modules?.map((module, idx) => {
            const isCompleted = module.completed;
            const icon = MODULE_ICONS[module.id] || '📚';
            
            return (
              <Card 
                key={module.id}
                className={`border-4 hover:shadow-2xl transition-all hover:-translate-y-2 cursor-pointer ${
                  isCompleted 
                    ? 'border-green-400 bg-green-50' 
                    : `${levelColors.border} bg-white`
                }`}
                onClick={() => startLesson(selectedLevel, module.id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl bg-gradient-to-br ${levelColors.bg} shadow-lg`}>
                      {icon}
                    </div>
                    {isCompleted ? (
                      <CheckCircle2 className="w-8 h-8 text-green-500" />
                    ) : (
                      <Badge className={`bg-gradient-to-r ${levelColors.bg} text-white`}>
                        {selectedLevel}
                      </Badge>
                    )}
                  </div>
                  
                  <h3 className="text-xl font-black text-gray-800 mb-1">{module.name}</h3>
                  <p className="text-sm text-gray-500 mb-3">{module.name_en}</p>
                  <p className="text-sm text-gray-600 mb-4">{module.description}</p>
                  
                  {/* AI Feature Badge */}
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    <span className="text-xs text-purple-600 font-medium">{module.ai_feature}</span>
                  </div>
                  
                  {/* Score if completed */}
                  {isCompleted && module.score > 0 && (
                    <div className="flex items-center gap-2 mb-4">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm font-bold text-yellow-600">Skor: {module.score}%</span>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button 
                      className={`flex-1 font-bold ${
                        isCompleted 
                          ? 'bg-green-500 hover:bg-green-600' 
                          : `bg-gradient-to-r ${levelColors.bg} hover:opacity-90`
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        startLesson(selectedLevel, module.id);
                      }}
                    >
                      {isCompleted ? 'Hızlı' : 'Başla'}
                    </Button>
                    <Button 
                      className="flex-1 font-bold bg-gradient-to-r from-purple-500 to-pink-600 hover:opacity-90 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/cabin-crew/comprehensive/${selectedLevel}/${module.id}`);
                      }}
                    >
                      <Crown className="w-4 h-4 mr-1" />
                      64 Soru
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* ICAO Alphabet Reference */}
        <Card className="mt-8 border-4 border-amber-300">
          <CardHeader className="bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-t-xl">
            <CardTitle className="flex items-center gap-3">
              <span className="text-2xl">🔤</span>
              <span>ICAO Havacılık Alfabesi Referans</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-9 gap-3">
              {Object.entries({
                "A": "Alfa", "B": "Bravo", "C": "Charlie", "D": "Delta",
                "E": "Echo", "F": "Foxtrot", "G": "Golf", "H": "Hotel",
                "I": "India", "J": "Juliet", "K": "Kilo", "L": "Lima",
                "M": "Mike", "N": "November", "O": "Oscar", "P": "Papa",
                "Q": "Quebec", "R": "Romeo", "S": "Sierra", "T": "Tango",
                "U": "Uniform", "V": "Victor", "W": "Whiskey", "X": "X-ray",
                "Y": "Yankee", "Z": "Zulu"
              }).map(([letter, word]) => (
                <div key={letter} className="bg-amber-50 rounded-lg p-2 text-center border-2 border-amber-200">
                  <p className="text-2xl font-black text-amber-600">{letter}</p>
                  <p className="text-xs font-medium text-gray-600">{word}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default CabinCrewModules;
